// React and Hooks
import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// Components
import PageHeader from '../../components/Custom/PageHeader/PageHeader'
import CustomToast from '../../components/Custom/CustomToast'

// Icons
import { IoArrowBackOutline, IoClose } from 'react-icons/io5'
import { FiUpload } from 'react-icons/fi'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'
import configManager from '../../config/configManager'

const JointUpdate = () => {
  const navigate = useNavigate()
  const goBack = useGoBack()
  const { id } = useParams()
  const configData = configManager()

  const fileInputRef = useRef(null)

  const [isUploading, setIsUploading] = useState(false)
  const [jointPreview, setJointPreview] = useState(null)
  const [jointFile, setJointFile] = useState(null)
  const [jointName, setJointName] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [realwidthMm, setRealwidthMm] = useState('0')

  const categories = useMemo(
    () => [
      'Architecture',
      'Landscaping',
      'Interior',
      'Exterior',
      'Other'
    ],
    []
  )

  const [errors, setErrors] = useState({})

  useEffect(() => {
    const fetchJointData = async () => {
      try {
        const response = await axiosInterceptorInstance.get(
          `/joints/details/${id}`,
        )
        if (!response.data.success) return

        const jointData = response.data.data
        setJointName(jointData.name)
        setSelectedCategory(jointData.subcategory || '')
        setJointPreview(
          jointData.thumbnail || jointData.source_names?.[0] || null,
        )
        setRealwidthMm(jointData.realwidth || '0')
      } catch (error) {
        CustomToast({
          message: 'Failed to fetch joint data. Please try again!',
          type: 'error',
        })
      }
    }

    fetchJointData()
  }, [id])

  const handleImageChange = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg']
      const maxSizeInMB = 5
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          jointFile: 'Only JPG, PNG, and JPEG formats are allowed.',
        }))
        return
      }

      if (file.size > maxSizeInBytes) {
        setErrors((prev) => ({
          ...prev,
          jointFile: `File size exceeds ${maxSizeInMB}MB limit.`,
        }))
        return
      }

      setJointFile(file)
      setJointPreview(URL.createObjectURL(file))
      setErrors((prev) => ({ ...prev, jointFile: null }))
    }
  }

  const handleDelete = () => {
    setJointFile(null)
    setJointPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const validateForm = () => {
    const newErrors = {}
    if (!jointName.trim()) newErrors.jointName = 'Joint name is required.'
    if (!selectedCategory) newErrors.selectedCategory = 'Please select a category.'
    if (!jointPreview) newErrors.jointFile = 'Please upload a joint file.'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('name', jointName)
      formData.append('category', 'Joint')
      formData.append('subcategory', selectedCategory)
      formData.append('file', jointFile)
      formData.append('type', 'Joint')
      formData.append('realwidth_mm', realwidthMm || '2000')
      await axiosInterceptorInstance.patch(`/joints/update/${id}`, formData)

      CustomToast({ message: 'Joint update successfully.', type: 'success' })
      setJointName('')
      handleDelete()
      navigate('/joint')
    } catch (error) {
      CustomToast({
        message: 'Failed to upload joint. Please try again!',
        type: 'error',
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={goBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Upload Joint</h1>
          </div>
        </div>
      </PageHeader>

      <div className='space-y-6 flex flex-col items-center'>
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>
            Upload Joint:
          </label>
          <div
            className={`relative w-full aspect-[9/5] rounded-lg bg-primary bg-opacity-5 cursor-pointer border ${errors.jointFile ? 'border-red-500' : 'border-dashed border-prime'
              } flex items-center justify-center`}
          >
            {!jointPreview && (
              <input
                accept='image/*'
                className='absolute inset-0 opacity-0 cursor-pointer'
                type='file'
                ref={fileInputRef}
                onChange={handleImageChange}
              />
            )}
            {jointPreview ? (
              <img
                src={
                  jointPreview.includes('blob')
                    ? jointPreview
                    : `${configData.CDN_BASE_URL}/${jointPreview.startsWith('/')
                      ? jointPreview.slice(1)
                      : `thumbnails/${jointPreview}`
                    }`
                }
                alt='Preview'
                className='absolute top-0 left-0 object-cover w-full h-full rounded-lg'
              />
            ) : (
              <div className='flex flex-col gap-4 items-center'>
                <FiUpload className='text-4xl text-white bg-prime rounded-full cursor-pointer p-2' />
                <span className='text-prime'>
                  Only JPG, PNG, and JPEG files are accepted
                </span>
              </div>
            )}
            {(jointFile || jointPreview) && (
              <button
                type='button'
                onClick={handleDelete}
                className='absolute top-2 right-2 flex items-center justify-center w-8 h-8 bg-prime bg-opacity-80 rounded-full text-primary bg-white'
              >
                <IoClose />
              </button>
            )}
            {errors.jointFile && (
              <p className='text-red-500 text-sm mt-1'>{errors.jointFile}</p>
            )}
          </div>
        </div>

        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>Select Category:</label>
          <select
            className={`w-full border ${errors.selectedCategory ? 'border-red-500' : 'border-gray-300'
              } rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime`}
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value)
              setErrors((prev) => ({ ...prev, selectedCategory: null }))
            }}
          >
            <option value=''>-- Select Category --</option>
            {categories.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {errors.selectedCategory && (
            <p className='text-red-500 text-sm mt-1'>{errors.selectedCategory}</p>
          )}
        </div>

        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>Joint Name:</label>
          <input
            type='text'
            placeholder='Joint Name'
            className={`w-full border ${errors.jointName ? 'border-red-500' : 'border-gray-300'
              } rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime`}
            value={jointName}
            onChange={(e) => {
              setJointName(e.target.value)
              setErrors((prev) => ({ ...prev, jointName: null }))
            }}
          />
          {errors.jointName && (
            <p className='text-red-500 text-sm mt-1'>{errors.jointName}</p>
          )}
        </div>

        {/* Joint Physical Width — controls scale/zoom on canvas */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='realwidth_mm'>
            Joint Physical Width (mm):
          </label>
          <input
            id='realwidth_mm'
            type='number'
            min='1'
            max='99999'
            placeholder='e.g. 2000'
            className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime'
            value={realwidthMm}
            onChange={(e) => setRealwidthMm(e.target.value)}
          />
          <div className='mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 space-y-1.5'>
            <p className='font-semibold text-gray-700'>
              💡 How to Set the Physical Width
            </p>
            <ul className='list-disc pl-4 space-y-1'>
              <li>
                <strong>Single Joint / Plank:</strong> If the image contains only one joint or plank, enter its actual physical width (e.g., <strong>300 mm</strong> or <strong>600 mm</strong>).
              </li>
              <li>
                <strong>Seamless Pattern:</strong> If the image shows multiple repeating tiles or bricks, enter the total width covered by the visible pattern (e.g., <strong>1000–1500 mm</strong>).
              </li>
              <li>
                <strong>Large Surfaces:</strong> For large seamless joint textures, use a standard physical width between <strong>2500–4000 mm</strong> (e.g., <strong>3000 mm</strong>).
              </li>
            </ul>
          </div>
        </div>

        {errors.upload && (
          <p className='text-red-500 text-sm mt-1'>
            {errors.upload || 'Something went wrong. Please try again.'}
          </p>
        )}

        <button
          disabled={isUploading}
          onClick={handleSubmit}
          className='w-full py-3 rounded-md sm:w-[500px] text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-auto disabled:bg-opacity-80 disabled:text-white'
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
    </div>
  )
}

export default JointUpdate
